import * as React from 'react';
import StyledModal from '../../Components/StyledModal';

import {
  Automation,
  AUTOMATION_SHELL_TYPE,
} from '../../../MedicareProAPI/Entities/Automation/Automation';
import {
  makeAutomationFromTemplate,
  AutomationTemplateBlueprints,
  AutomationTemplate,
} from '../../Utilities/AutomationTemplateMaker';

interface AutomationSelectorModalProps {
  userId: number;
  userAccessToken: string;
  open: boolean;
  onClose: () => void;
  onTemplateSelect: (type: AUTOMATION_SHELL_TYPE, automation?: any) => void;
}

/**
 * Modal component for selecting starter automation (blank, starter)
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-08-26
 * @lastUpdated
 * @version 1.1.0
 */
export const AutomationSelectorModal = ({
  userId,
  userAccessToken,
  open,
  onClose,
  onTemplateSelect,
}: AutomationSelectorModalProps): React.JSX.Element => {
  const [creatingTemplate, setCreatingTemplate] = React.useState<boolean>(false);
  const [typeSelected, setTypeSelected] = React.useState<AUTOMATION_SHELL_TYPE>(
    AUTOMATION_SHELL_TYPE['blank']
  );
  const [starterTemplateIndex, setStarterTemplateIndex] = React.useState<number>();
  const [starterTemplateSelected, setStarterTemplateSelected] = React.useState<any>();

  const handleStarterTemplateChosen = async (
    typeSelected: AUTOMATION_SHELL_TYPE,
    index: number,
    userId: number,
    userAccessToken: string
  ): Promise<any> => {
    setCreatingTemplate(true);
    console.log(index);
    const newAutomation = await makeAutomationFromTemplate(index, userId, userAccessToken);
    setTypeSelected(AUTOMATION_SHELL_TYPE.BLANK);
    setCreatingTemplate(false);
    onTemplateSelect(typeSelected, newAutomation);
  };

  const handleStarterTemplateSelected = (index: number) => {
    setStarterTemplateIndex(index);
    setStarterTemplateSelected(AutomationTemplateBlueprints[index]);
  };

  const handleBlankTemplateSelected = () => {
    setTypeSelected(AUTOMATION_SHELL_TYPE.BLANK);
    onTemplateSelect(AUTOMATION_SHELL_TYPE.BLANK);
  };

  return (
    <StyledModal
      size="xl"
      title="Automation Builder"
      footerCloseButton
      show={open}
      onClose={onClose}
    >
      {/* Top choices */}
      <div className="row mb-3">
        {/* Blank Template */}
        <div className="col">
          <div
            className="card border cp pt-3 pb-2 border-secondary mb-2 templateStartOption w-100 text-left"
            data-type="blank"
            onClick={handleBlankTemplateSelected}
            style={{ height: 'auto' }}
          >
            <div className="card-body text-center">
              <h5>
                <i className="fas fa-file text-secondary" />
              </h5>
              <div>
                <b>Blank Template</b>
              </div>
              <p className="mb-0" style={{ fontSize: '9pt' }}>
                <i>Start from scratch and design your own automation</i>
              </p>
            </div>
          </div>
        </div>

        {/* Starter Template */}
        <div className="col">
          <div
            className="card border cp pt-3 pb-2 border-info mb-2 templateStartOption w-100 text-left"
            data-type="starter"
            onClick={() => setTypeSelected(AUTOMATION_SHELL_TYPE.STARTER)}
            style={{ height: 'auto' }}
          >
            <div className="card-body text-center">
              <h5>
                <i className="fas fa-file-alt text-info" />
              </h5>
              <div>
                <b>Starter Template</b>
              </div>
              <p className="mb-0" style={{ fontSize: '9pt' }}>
                <i>Start with a pre-made template and edit to your liking.</i>
              </p>
            </div>
          </div>
        </div>

        {/* Shared Template (future)
      { (uplineId || absoluteUplineId) && (
        <div className="col">
          <button
            type="button"
            className="card border cp pt-3 pb-2 border-primary mb-2 templateStartOption w-100 text-left"
            data-type="shared"
            onClick={() => setTypeSelected(AUTOMATION_SHELL_TYPE.SHARED)}
          >
            <div className="card-body text-center">
              <h5><i className="fas fa-user text-primary" /></h5>
              <div><b>Shared Template</b></div>
              <p className="mb-0" style={{ fontSize: '9pt' }}>
                <i>Start with a template shared by your upline.</i>
              </p>
            </div>
          </button>
        </div>
      )}
      */}
      </div>

      {/* Side panels */}
      <div className="row">
        <div className="col-4 templateOptionsReturn">
          {typeSelected === AUTOMATION_SHELL_TYPE.STARTER && (
            <h6 className="font-weight-bold mb-2 mt-2">Templates</h6>
          )}
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {typeSelected === AUTOMATION_SHELL_TYPE.STARTER &&
              AutomationTemplateBlueprints.map((template, idx) => (
                <div
                  className="rounded mb-2 p-2 border border-info cp"
                  key={template?.metaData?.slug ?? idx}
                  onClick={() => handleStarterTemplateSelected(idx)}
                  role="button"
                  tabIndex={0}
                >
                  <div>
                    <i className="far fa-file-alt text-primary mr-2" />
                    {template.automation.name}
                    <span
                      className="badge badge-pill table-success text-dark ml-2"
                      style={{ fontSize: '8pt' }}
                    >
                      {template.metaData.category}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="col-8 border-left templateChoserDisplay">
          {typeSelected === AUTOMATION_SHELL_TYPE.STARTER && starterTemplateSelected && (
            <>
              <div className="mb-2 font-weight-bold mt-2">
                {starterTemplateSelected.automation.name}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary templateChosen starterTemplate mb-2"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleStarterTemplateChosen(
                    AUTOMATION_SHELL_TYPE.STARTER,
                    starterTemplateIndex,
                    userId,
                    userAccessToken
                  );
                }}
                disabled={creatingTemplate}
              >
                {creatingTemplate ? 'Creating…' : 'Choose this template'}
              </button>
              <div className="rounded bg-contrast p-3">
                <i className="far fa-sparkles text-info mr-2"></i>
                {starterTemplateSelected.metaData.description}
                <p className="mt-3">
                  <b>This automation will also make you the following message templates:</b>
                  <ul className="mt-2">
                    {starterTemplateSelected.metaData.templatesCreated.map((item) => {
                      return (
                        <li>
                          <i className="far fa-file-alt text-primary mr-2"></i>
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </p>
              </div>
              <p className="mt-4">
                <i>
                  <b>NOTE:</b> Starter templates give you a solid foundation. Once selected, you can
                  customize everything—triggers, actions, timing, and the generated message
                  templates. All starter templates default to using email as the action method but
                  you can change that to SMS, ringless voicemail, or mail if you have those sort of
                  services integrated. The automation will also be set as "inactive". Change it to
                  "Active" when you are ready to go.
                </i>
              </p>
            </>
          )}
        </div>
      </div>
    </StyledModal>
  );
};
